import { AlertTriangle } from 'lucide-react';
import type { PreflightHardFailure, PreflightWarningRow } from '@signalvote/shared';
import { PreflightContactName } from './PreflightContactName';

interface HardFailureListProps {
  hardFailures: PreflightHardFailure[];
}

/** How many donor names to spell out before collapsing the rest into a "+N more" count. */
const MAX_NAMED_DONORS = 3;

/**
 * The affected donors, resolved from a blocker's enriched {@link PreflightWarningRow}s so the
 * treasurer can tell WHO to fix, not just how many (issue #213). Deduplicates a donor named across
 * several rows, drops rows with no resolvable name (e.g. the missing-name case has nothing to show —
 * the failure message's count stands), and caps the list. Returns null when nothing is nameable.
 */
function affectedDonors(
  rows: PreflightWarningRow[] | null,
): { named: PreflightWarningRow[]; moreCount: number } | null {
  if (!rows || rows.length === 0) return null;
  const byDonor = new Map<string, PreflightWarningRow>();
  for (const r of rows) {
    const name = r.contactName.trim();
    if (name.length === 0) continue;
    const key = r.contactId ?? name;
    if (!byDonor.has(key)) byDonor.set(key, { ...r, contactName: name });
  }
  if (byDonor.size === 0) return null;
  const donors = Array.from(byDonor.values());
  return {
    named: donors.slice(0, MAX_NAMED_DONORS),
    moreCount: Math.max(0, donors.length - MAX_NAMED_DONORS),
  };
}

/**
 * AFU-006 — the read-only "blocking issues — must fix before filing" list.
 *
 * Extracted verbatim from `FilePreflightModal`'s inline JSX so the File modal
 * and the close hub (`CloseHubPreflight`) render the SAME hard-failure list
 * and can never drift. Pure presentation: it owns no disposition state (unlike
 * `HeadsUpCard`, which is warnings-only and DOES own dispositions) — hard
 * failures are not attestable, they must be resolved before the file can emit.
 *
 * Returns `null` when there are no hard failures so callers can mount it
 * unconditionally.
 */
export function HardFailureList({ hardFailures }: HardFailureListProps) {
  if (hardFailures.length === 0) return null;

  return (
    <div
      className="rounded-md border border-red-500 bg-red-50 p-3 text-sm text-red-900"
      data-testid="preflight-hard-failures"
      role="alert"
    >
      <div className="mb-2 flex items-center gap-1 font-medium">
        <AlertTriangle className="h-4 w-4" />
        {hardFailures.length} blocking issue{hardFailures.length === 1 ? '' : 's'} —
        must fix before filing
      </div>
      <ul className="space-y-1 text-xs">
        {hardFailures.map((f) => {
          const donors = affectedDonors(f.affectedRows);
          return (
            <li key={f.code} data-testid={`hard-failure-${f.code}`}>
              {f.message}
              {donors && (
                <span
                  className="mt-0.5 block font-medium"
                  data-testid={`hard-failure-donors-${f.code}`}
                >
                  {donors.named.map((d, i) => (
                    <span key={d.contactId ?? d.contactName}>
                      {i > 0 && ', '}
                      <PreflightContactName name={d.contactName} contactId={d.contactId} />
                    </span>
                  ))}
                  {donors.moreCount > 0 && `, and ${donors.moreCount} more`}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
